import { Link as RouterLink } from 'react-router';

import { RefinancingHero } from '@/components/RefinancingHero/index.ts';
import { Button } from '@/ui/button-2.tsx';

import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_NOMINAL_AND_EFFECTIVE_INTEREST_RATE } from './RRS.ts';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const linkClassNameSubtitle2 =
  'typography-v1-legacy-subtitle2 font-normal text-[#18A0FB]! no-underline hover:underline';

const chatTriggerButtonClassName = `${linkClassName} inline bg-transparent border-0 p-0 cursor-pointer text-left font-inherit`;

const articleRowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

const nominalTableWrapClassName =
  'relative mx-auto mb-12 w-full max-w-[900px]! min-w-0 basis-full self-stretch overflow-x-auto rounded-lg border border-[#CEE2FF] shadow-[0px_2px_4px_rgba(0,0,0,0.02)] touch-pan-x';

const debtCheckButtonClassName = 'max-w-[550px] self-center sm:min-w-[330px]';

type Row = {
  lender: string;
  amount: string;
  repaymentTime: string;
  establishmentFee: string;
  termFee: string;
  nominalInterestRate: string;
  effectiveInterestRate: string;
  monthCost: string;
  totalCost: string;
};

const tableColumns: {
  id: keyof Row;
  label: string;
  align: 'left' | 'right';
  minWidth: number;
}[] = [
  {
    id: 'lender',
    label: 'Långiver',
    align: 'left',
    minWidth: 100,
  },
  {
    id: 'amount',
    label: 'Beløp',
    align: 'right',
    minWidth: 140,
  },
  {
    id: 'repaymentTime',
    label: 'Nedbetalingstid',
    minWidth: 150,
    align: 'right',
  },
  {
    id: 'establishmentFee',
    label: 'Etableringsgebyr',
    minWidth: 150,
    align: 'right',
  },
  {
    id: 'termFee',
    label: 'Termingebyr',
    minWidth: 120,
    align: 'right',
  },
  {
    id: 'nominalInterestRate',
    label: 'Nominell rente',
    minWidth: 140,
    align: 'right',
  },
  {
    id: 'effectiveInterestRate',
    label: 'Effektiv rente',
    minWidth: 130,
    align: 'right',
  },
  {
    id: 'monthCost',
    label: 'Måndelig kostnad',
    minWidth: 170,
    align: 'right',
  },
  {
    id: 'totalCost',
    label: 'Totalkostnad',
    minWidth: 150,
    align: 'right',
  },
];

function createData(
  lender: string,
  establishmentFee: string,
  termFee: string,
  nominalInterestRate: string,
  effectiveInterestRate: string,
  monthCost: string,
  totalCost: string,
): Row {
  const amount = '150 00000 kr';
  const repaymentTime = '5 år';
  return {
    lender,
    amount,
    repaymentTime,
    establishmentFee,
    termFee,
    nominalInterestRate,
    effectiveInterestRate,
    monthCost,
    totalCost,
  };
}

const banks = [
  createData('Bank1', '950,00 kr', '70,00 kr', '7,9%', '9,5%', '3 124 00 kr', '187 440,00 kr'),
  createData('Bank2', '497,00 kr', '29,00 kr', '8,4%', '9,9%', '3 142 00 kr', '189 017,00 kr'),
  createData('Bank3', '890,00 kr', '45,00 kr', '8,4%', '9,6%', '3 133 00 kr', '187 980,00 kr'),
  createData('Bank4', '900,00 kr', '40,00 kr', '10,7%', '12,3%', '3 290 00 kr', '198 300,00 kr'),
  createData('Bank5', '950,00 kr', '40,00 kr', '13,9%', '15,7%', '3 545 00 kr', '213 650,00 kr'),
];

const NominalInterestComparisonTable = () => (
  <div className={nominalTableWrapClassName}>
    <table className="w-full min-w-[920px] border-collapse border-hidden">
      <thead>
        <tr className="align-top">
          {tableColumns.map((column) => {
            const isFirstColumn = column.id === 'lender';
            return (
              <th
                key={column.id}
                scope="col"
                style={{ minWidth: column.minWidth }}
                className={
                  isFirstColumn
                    ? 'sticky left-0 z-10 w-[100px] min-w-[100px] border border-[#CEE2FF] bg-white px-4! py-2! text-left align-top typography-v1-legacy-table-cell font-normal text-v1-background-200'
                    : 'border border-[#CEE2FF] px-4! py-2! text-right align-top typography-v1-legacy-table-cell font-normal text-[#18A0FB]'
                }
              >
                {isFirstColumn ? (
                  <span
                    className="absolute top-[-8px] left-[100px] h-[253px] w-px bg-[#CEE2FF]"
                    aria-hidden
                  />
                ) : null}
                {column.label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {banks.map((row) => (
          <tr key={row.lender} tabIndex={-1} className="align-top">
            {tableColumns.map((column) => {
              const value = row[column.id];
              const isFirstColumn = column.id === 'lender';
              const sharedCellStyle = { minWidth: column.minWidth };
              if (isFirstColumn) {
                return (
                  <th
                    key={column.label}
                    scope="row"
                    style={sharedCellStyle}
                    className="sticky left-0 z-10 w-[100px] min-w-[100px] border border-[#CEE2FF] bg-white px-4! py-2! text-left align-top typography-v1-legacy-table-cell font-normal text-v1-background-200"
                  >
                    {value}
                  </th>
                );
              }
              return (
                <td
                  key={column.label}
                  style={sharedCellStyle}
                  className="border border-[#CEE2FF] px-4! py-2! text-right align-top typography-v1-legacy-table-cell font-normal text-[#18A0FB]"
                >
                  {value}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NominalAndEffectiveInterestRate: React.FC = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  const handleDebtCheckClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  const openDebtCheckChat = () => {
    handleDebtCheckClick();
  };

  return (
    <Page title={appStrings.MENU.NOMINAL_AND_EFFECTIVE_INTEREST_RATE}>
      <RrsHandler {...RRS_NOMINAL_AND_EFFECTIVE_INTEREST_RATE} />
      <RefinancingHero chatStep="refin_present_terms_auto_new" />
      <Section>
        <div className="flex max-w-[900px]! flex-col">
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Nominell og effektiv rente – hva er forskjellen?
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Alle som søker om lån ønsker seg så lav rente som mulig. Men det er fort gjort å la seg
            lure, for rente er ikke bare rente. Det kan være store forskjeller på den effektive og
            den nominell renten – vi forteller deg hvordan du unngår å gå i rentefellen.
          </h2>
          <p className="typography-v1-legacy-subtitle2">
            Du lurer kanskje på hva rente er? Det er rett og slett betalingen banken tar for å låne
            deg penger. Og hvilken betaling de tar, avhenger blant annet av inntekt, formue,
            eiendeler og tidligere gjeld, og hvilken sikkerhet og hvilke verdier du kan stille med.
            <br />
            <br />
            Nettopp derfor har boliglån lav rente, fordi du stiller sikkerhet i boligen sin som
            sannsynligvis vil stige i verdi. Også billån har relativt lav rente selv om bilen din
            sannsynligvis synker i verdi, men den er likevel en sikkerhet for banken.
            <br />
            <br />
            I motsatt ende av renteskalaen finner du forbrukslån, kjøpskreditter og kredittkort, og
            det er fordi du ikke behøver å stille sikkerhet. Dette medfører at bankene tar større
            risiko for å låne deg penger, og denne risikoen viser seg altså i høyere rente.
            <br />
            <br />
            <strong>
              For å få opplysninger og eventuelt kunne godkjenne lånesøknaden din, sjekker bankene
              blant annet{' '}
              <RouterLink className={linkClassNameSubtitle2} to={routes.DEBT_REGISTER}>
                Gjeldsregisteret – det kan du også gjøre.
              </RouterLink>
            </strong>
          </p>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={articleRowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Så, hva er forskjellen på nominell og effektiv rente?
            </h3>
            <p className="typography-v1-legacy-p3">
              Først må vi forklare hva nominell og effektiv rente er:
            </p>
            <ul className="typography-v1-legacy-list typography-v1-legacy-p3">
              <li>
                <strong>Nominell rente</strong> er uten avgifter, gebyrer og andre kostnader. Den er
                altså kun grunnrenten, og sier ingenting om hva lånet ditt faktisk koster deg.{' '}
              </li>
              <li>
                <strong>Effektiv rente</strong> er inkludert avgifter, gebyrer og andre kostnader.
                Den er altså den totale renten, og forteller deg derfor hva lånet ditt faktisk
                koster deg.{' '}
              </li>
            </ul>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Noen banker har få avgifter og lave kostnader, mens andre tar vesentlig mer og i
              tillegg kan være i overkant rause med antall gebyrer og prisen de tar for dem. Derfor
              gjelder det å finne riktig bank når du skal søke om forbrukslån. Husk for øvrig at
              alle banker plikter å gi deg både nominell og effektiv rente når de gir deg et
              lånetilbud.
              <br />
              <br />
              <strong>Hva vil lånet koste deg? Sjekk forbrukslånskalkulatoren vår!</strong>
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvor store forskjeller kan det bli på nominell og effektiv rente?
            </h3>
            <p className="typography-v1-legacy-p3">
              Kort forklart: Store. Og det er derfor du må se nøye på hva den effektive renten er.
              For når du tar opp lån må du ta med alle kostnader i regnestykket. Gjør du ikke det,
              kan det koste deg tusenvis av kroner.
            </p>
            <ul className="typography-v1-legacy-list typography-v1-legacy-p3">
              <li>
                Det kan være lett å la seg blende av en bank som gir deg 9,9 prosent nominell rente
                på forbrukslånet ditt, uten å tenke over at den effektive er 14,5.
              </li>
              <li>
                Og motsatt – det er fort gjort å velge vekk en bank som gir deg 12,2 prosent
                nominell rente på forbrukslånet ditt, selv om den effektive er 13,4 prosent.
              </li>
            </ul>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              I tabellen under ser du at forskjellen på nominell og effektiv rente kan utgjøre mye
              penger per måned og over tid.
            </p>
          </div>

          <NominalInterestComparisonTable />

          <div className="w-full sm:w-1/2 sm:pr-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Forvirret? Du er ikke den eneste. I tillegg har det blitt mange tilbydere av lån, og
              det blir stadig flere. Dette gjør det samtidig vanskeligere å finne frem til det beste
              tilbudet, da spesielt forbrukslån har store forskjeller på renten. Det kan med andre
              ord vise seg å være en umulig oppgave å finne det beste tilbudet, og mange av oss
              bruker både dager og uker på å lete på nettet, sende søknader og sammenligne tilbud.
              <br />
              <br />
              <strong>
                Men – hvorfor skal du gjøre jobben med å lete, sende og sammenligne når Scoopr gjør
                det for deg?
              </strong>
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Forbrukslån med lav effektiv rente? Prøv Scoopr!
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Når du søker om forbrukslån hos oss, sendes søknaden din automatisk til 13 banker som
              alle konkurrerer om å gi deg den laveste effektive renten. Du sparer tid og penger, og
              det er gratis og uforpliktende. Å søke tar deg to minutter, og du gjør det i tre enkle
              steg:
            </p>
          </div>
          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              <strong>Søke</strong>
              <br />
              Finn frem BankID-en din,{' '}
              <button
                type="button"
                className={chatTriggerButtonClassName}
                onClick={openDebtCheckChat}
              >
                trykk her og søk om lån fra 10.000 til 600.000 kroner.
              </button>{' '}
              Så snart du har sendt søknaden er jobben din gjort, og Scoopr og bankene tar over
              stafettpinnen.
              <br />
              <br />
              <strong>Sammenligne</strong>
              <br />
              Når du har mottatt tilbudene har du god tid på å bestemme deg, siden de gjelder i 30
              dager. Da får du tiden du trenger til å vurdere hvilket som passer deg og økonomien
              din best.
              <br />
              <br />
              <strong>Velge</strong>
              <br />
              Etter at du har valgt banken din, er som regel pengene på konto i løpet av et par
              dager. Og du husket vel å se på den effektive renten før du bestemte deg?
              <br />
              <br />
              <strong>
                Ønsker du å søke om forbrukslån hos Scoopr?{' '}
                <button
                  type="button"
                  className={chatTriggerButtonClassName}
                  onClick={openDebtCheckChat}
                >
                  Kun to minutter nå, kan spare deg for tusenvis av kroner i måneden.
                </button>
              </strong>
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <Button
            color="yellow"
            className={debtCheckButtonClassName}
            onClick={handleDebtCheckClick}
          >
            Sjekk nå
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default NominalAndEffectiveInterestRate;
